/**
 * Web-only Stripe checkout modal.
 *
 * Mounted on the web build of OneClickBookingScreen to charge the user with a
 * real Stripe PaymentIntent (card / Apple Pay / Google Pay / Link, picked by
 * the unified PaymentElement based on browser support).
 *
 * Renders DOM-level Stripe React Elements inside a react-native-web Modal so it
 * works inside the embedded widget without altering native (iOS/Android) flows.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  ScrollView,
  Platform,
} from 'react-native';
import {
  createAppointmentPaymentIntent,
  confirmAppointmentPayment,
  getStripe,
  type CreateAppointmentIntentInput,
} from '../services/webStripeCheckout';

// Stripe Elements components are only imported / rendered on the web build,
// so this module is safe to ship in the React Native bundle as well.
let StripeElementsLib: any = null;
async function loadStripeElementsLib() {
  if (!StripeElementsLib) {
    StripeElementsLib = await import('@stripe/react-stripe-js');
  }
  return StripeElementsLib;
}

export interface WebStripeCheckoutResult {
  success: boolean;
  paymentIntentId?: string;
  error?: string;
  cancelled?: boolean;
}

interface WebStripeCheckoutModalProps {
  visible: boolean;
  intentInput: CreateAppointmentIntentInput | null;
  /** Display label like "Apple Pay", "Card" — used for the submit button copy. */
  preferredMethodLabel?: string;
  onClose: () => void;
  onResult: (result: WebStripeCheckoutResult) => void;
}

const PaymentForm: React.FC<{
  clientSecret: string;
  amount: number;
  serviceName: string;
  preferredMethodLabel?: string;
  onResult: (result: WebStripeCheckoutResult) => void;
  onClose: () => void;
}> = ({ clientSecret, amount, serviceName, preferredMethodLabel, onResult, onClose }) => {
  const [lib, setLib] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadStripeElementsLib().then((mod) => {
      if (!cancelled) setLib(mod);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!lib) {
    return (
      <View style={styles.loadingState}>
        <ActivityIndicator size="small" color="#007AFF" />
        <Text style={styles.loadingText}>Loading secure checkout…</Text>
      </View>
    );
  }

  const { PaymentElement, useStripe, useElements } = lib;

  const InnerForm: React.FC = () => {
    const stripe = useStripe();
    const elements = useElements();

    const handlePay = async () => {
      if (!stripe || !elements) return;
      setError(null);
      setSubmitting(true);

      const result = await confirmAppointmentPayment({ stripe, elements });
      setSubmitting(false);

      if (result.success) {
        onResult(result);
      } else {
        setError(result.error || 'Payment failed');
        if (result.cancelled) {
          // Stay open – the user just dismissed the wallet sheet.
          return;
        }
      }
    };

    return (
      <View style={styles.formInner}>
        {/* PaymentElement is a real <div>; wrap it so RN-Web layout plays nice. */}
        <View style={styles.elementHost} dataSet={{ stripeElementHost: '1' } as any}>
          <PaymentElement
            options={{
              layout: 'tabs',
              wallets: { applePay: 'auto', googlePay: 'auto' },
            }}
          />
        </View>

        {error ? <Text style={styles.errorText}>{error}</Text> : null}

        <TouchableOpacity
          style={[styles.payButton, submitting && styles.payButtonDisabled]}
          onPress={handlePay}
          disabled={submitting || !stripe || !elements}
          accessibilityRole="button"
          accessibilityLabel={`Pay $${amount.toFixed(2)}`}
        >
          {submitting ? (
            <ActivityIndicator size="small" color="#fff" />
          ) : (
            <Text style={styles.payButtonText}>
              {preferredMethodLabel
                ? `Pay $${amount.toFixed(2)} with ${preferredMethodLabel}`
                : `Pay $${amount.toFixed(2)}`}
            </Text>
          )}
        </TouchableOpacity>

        <Text style={styles.disclaimer}>
          Secured by Stripe · {serviceName}
        </Text>
      </View>
    );
  };

  const { Elements } = lib;
  return (
    <Elements
      stripe={getStripe()}
      options={{
        clientSecret,
        appearance: { theme: 'stripe', variables: { colorPrimary: '#007AFF' } },
      }}
    >
      <InnerForm />
      <TouchableOpacity style={styles.cancelButton} onPress={onClose} disabled={submitting}>
        <Text style={styles.cancelButtonText}>Cancel</Text>
      </TouchableOpacity>
    </Elements>
  );
};

const WebStripeCheckoutModal: React.FC<WebStripeCheckoutModalProps> = ({
  visible,
  intentInput,
  preferredMethodLabel,
  onClose,
  onResult,
}) => {
  const [loadingIntent, setLoadingIntent] = useState(false);
  const [slowLoad, setSlowLoad] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [setupError, setSetupError] = useState<string | null>(null);

  // Reset on every open with new input.
  useEffect(() => {
    if (!visible) {
      setClientSecret(null);
      setSetupError(null);
      setSlowLoad(false);
      return;
    }
    if (!intentInput) return;
    let cancelled = false;
    setLoadingIntent(true);
    setSetupError(null);
    setClientSecret(null);
    setSlowLoad(false);

    // After 6s of waiting, surface the slow-load message. This typically
    // only fires when the Render payment API is cold-starting (~20-30s).
    const slowTimer = setTimeout(() => {
      if (!cancelled) setSlowLoad(true);
    }, 6000);

    createAppointmentPaymentIntent(intentInput)
      .then((intent) => {
        if (cancelled) return;
        setClientSecret(intent.clientSecret);
      })
      .catch((err: any) => {
        if (cancelled) return;
        console.error('WebStripeCheckoutModal: failed to create intent', err);
        setSetupError(
          err?.requiresConnect
            ? 'This business has not finished setting up payments yet. Please contact them.'
            : err?.message || 'Unable to start checkout. Please try again.'
        );
      })
      .finally(() => {
        clearTimeout(slowTimer);
        if (!cancelled) {
          setLoadingIntent(false);
          setSlowLoad(false);
        }
      });

    return () => {
      cancelled = true;
      clearTimeout(slowTimer);
    };
  }, [visible, intentInput]);

  const amount = useMemo(() => Number(intentInput?.amount || 0), [intentInput]);
  const serviceName = intentInput?.serviceName || 'Appointment';

  // Only render meaningful content on web; on native we no-op.
  if (Platform.OS !== 'web') return null;

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View style={styles.sheet}>
          <View style={styles.header}>
            <Text style={styles.title}>Complete your booking</Text>
            <TouchableOpacity onPress={onClose} accessibilityLabel="Close checkout">
              <Text style={styles.close}>✕</Text>
            </TouchableOpacity>
          </View>

          <Text style={styles.subtitle}>
            {serviceName} · ${amount.toFixed(2)}
          </Text>

          <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent}>
            {loadingIntent ? (
              <View style={styles.loadingState}>
                <ActivityIndicator size="large" color="#007AFF" />
                <Text style={styles.loadingText}>Preparing secure checkout…</Text>
                {slowLoad ? (
                  <Text style={styles.slowLoadText}>
                    Waking up the payment server. This can take ~20–30s on the
                    first booking of the day.
                  </Text>
                ) : null}
                <TouchableOpacity style={styles.cancelButton} onPress={onClose}>
                  <Text style={styles.cancelButtonText}>Cancel</Text>
                </TouchableOpacity>
              </View>
            ) : setupError ? (
              <View style={styles.errorState}>
                <Text style={styles.errorText}>{setupError}</Text>
                <TouchableOpacity style={styles.cancelButton} onPress={onClose}>
                  <Text style={styles.cancelButtonText}>Close</Text>
                </TouchableOpacity>
              </View>
            ) : clientSecret ? (
              <PaymentForm
                clientSecret={clientSecret}
                amount={amount}
                serviceName={serviceName}
                preferredMethodLabel={preferredMethodLabel}
                onResult={onResult}
                onClose={onClose}
              />
            ) : null}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  sheet: {
    width: '100%',
    maxWidth: 460,
    maxHeight: '90%',
    backgroundColor: 'white',
    borderRadius: 16,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.2,
    shadowRadius: 24,
    elevation: 12,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingTop: 18,
  },
  title: { fontSize: 18, fontWeight: '700', color: '#1C1C1E' },
  close: { fontSize: 22, color: '#8E8E93', paddingHorizontal: 8 },
  subtitle: {
    paddingHorizontal: 20,
    paddingBottom: 12,
    color: '#3A3A3C',
    fontSize: 14,
    fontWeight: '500',
  },
  body: { paddingHorizontal: 20 },
  bodyContent: { paddingBottom: 20 },
  loadingState: { paddingVertical: 32, alignItems: 'center', gap: 12 },
  loadingText: { color: '#6C6C70', fontSize: 14 },
  slowLoadText: {
    color: '#8E8E93',
    fontSize: 12,
    textAlign: 'center',
    paddingHorizontal: 8,
    lineHeight: 16,
  },
  errorState: { paddingVertical: 16 },
  errorText: { color: '#B42318', fontSize: 13, marginVertical: 8 },
  formInner: { gap: 16 },
  elementHost: {
    paddingVertical: 4,
  },
  payButton: {
    backgroundColor: '#007AFF',
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  payButtonDisabled: { opacity: 0.6 },
  payButtonText: { color: 'white', fontWeight: '700', fontSize: 16 },
  disclaimer: { fontSize: 11, color: '#8E8E93', textAlign: 'center' },
  cancelButton: {
    marginTop: 12,
    paddingVertical: 10,
    alignItems: 'center',
  },
  cancelButtonText: { color: '#007AFF', fontSize: 14, fontWeight: '600' },
});

export default WebStripeCheckoutModal;
